// @ts-check
import React from 'react';
import { Box, useTheme } from '../../pdf-design-system';

/**
 * A set of columns.
 * You can also use arbitrary elements as children.
 *
 * @param {{
 *  space?: number;
 *  columns?: number;
 *  wrap?: boolean | 'wrap' | 'wrap-reverse' | 'nowrap';
 *  style?: import('@react-pdf/types').Style;
 *  children: React.ReactNode;
 *  [x: string]: any;
 * }} props
 * @returns
 */
export default function Columns({
  space = 2,
  columns,
  style,
  children,
  wrap,
  debug,
  ...rest
}) {
  const { theme } = useTheme();
  const spacing = theme.space?.[space];
  const allCols = React.Children.toArray(children);
  const childCols =
    columns ||
    allCols
      // @ts-ignore Not all children have props.
      .map((child) => child?.props?.span || 1)
      .reduce((total, span) => total + span, 0);
  const flexWrap = wrap === true ? 'wrap' : wrap === false ? 'nowrap' : wrap;
  return (
    <Box
      style={[
        {
          display: 'flex',
          flexDirection: 'row',
          marginHorizontal: spacing != null && `-${spacing}`,
          flexWrap: flexWrap ?? columns ? 'wrap' : undefined,
        },
        style,
      ]}
      {...rest}
    >
      {allCols
        // @ts-ignore Not all children have props.
        .filter((child) => !child?.props?.hide)
        .map((child) => (
          <Column
            // @ts-ignore Not all children have keys.
            key={child?.key}
            debug={debug}
            spacing={spacing}
            columns={childCols}
            child={child}
          />
        ))}
    </Box>
  );
}

function Column({ spacing, child, columns, debug }) {
  if (!child) return child;
  const { width, span = 1, style } = child.props || {};
  return (
    <Box
      debug={debug}
      style={{
        paddingHorizontal: spacing,
        width: width || style?.width || `${(span / columns) * 100}%`,
        display: 'flex', // Allow us to fill the complete width.
        alignItems: 'stretch', // Stretch the component horizontally.
        // @ts-ignore stretch isn't defined on the style types, but it seems to work.
        justifyContent: 'stretch', // Stretch the component vertically.
      }}
    >
      {React.cloneElement(child, {
        //   Remove the props only useful for Columns.
        width: undefined,
        span: undefined,
      })}
    </Box>
  );
}
